import { KeyboardArrowDown, KeyboardArrowUp, Sort } from '@mui/icons-material'
import { Grid, Typography } from '@mui/material'
import Menu from '@mui/material/Menu'
import { useEffect, useState } from 'react'
import { MUIButton, MUICheckbox, SimpleDropdown } from '..'
import CONSTANTS from '../../../Constants'
import { isEnableSubmit } from '../../../Helpers/Util'
import MUIDateTimePicker from '../../MUIDateTimePicker'
import { FilterSearch } from './FilterSearch'
import './index.scss'

const buttonsObj = { textTransform: 'capitalize', size: 'large', type: 'button', width: '20%' }
const buttons = [
  {
    ...buttonsObj,
    variant: 'outlined',
    label: 'Cancel',
    clickAction: 'cancel',
    color: 'secondary',
  },
  { ...buttonsObj, variant: 'contained', label: 'Submit', clickAction: 'submit' },
]

/**
 * A component that provides a searchable list with checkboxes and submit/cancel buttons in a dropdown menu.
 * @param title - The title displayed in the dropdown button.
 * @param listItems - An array of items to display in the list.
 * @param onUpdateListItems - A function to update the list items on submit.
 * @param showLoaderFn - A function to show/hide loader.
 * @param onClose - A function called on closing the dropdown.
 * @param search - The placeholder text for the search input.
 */
export const FilterSearchWithList = ({
  title,
  listItems,
  onUpdateListItems,
  showLoaderFn,
  onClose,
  search = '',
  showBorder = false,
  dropdownTitle = '',
  clear = false,
  key = '',
  isPopup = false,
  dateTypeFilter = false,
  filterOptions = [],
  onUpdateDate,
  selectedDateFilter = [],
  filterConditionLabel = '',
  isMultiple = true, // New prop with default value
}: {
  title: string
  listItems: any[]
  onUpdateListItems: (data: any[]) => any
  showLoaderFn?: (isLoading: boolean) => void
  onClose?: () => void
  search?: string
  showBorder?: boolean
  dropdownTitle?: string
  clear?: boolean
  key?: string
  isPopup?: boolean
  dateTypeFilter?: boolean
  filterOptions?: any[]
  onUpdateDate?: any
  selectedDateFilter?: any
  filterConditionLabel?: string
  isMultiple?: boolean // New prop type
}) => {
  const [anchorColEl, setAnchorColEl] = useState<null | EventTarget>(null)
  const [colListTemp, setColListTemp] = useState<any>([])
  const [searchInput, setSearchInput] = useState<any>('')
  const [filterdata, setFilterData] = useState<any>({
    filterBy: '',
    filterValue: '',
    filterCondition: 'equals',
  })

  useEffect(() => {
    if (dateTypeFilter && selectedDateFilter?.length) {
      setFilterData(selectedDateFilter[0])
    } else if (dateTypeFilter) {
      setFilterData({
        filterBy: '',
        filterValue: '',
        filterCondition: 'equals',
      })
    }
  }, [anchorColEl])

  const handleClose = (type: any) => {
    if (type === 'submit') {
      !dateTypeFilter && onUpdateListItems([...colListTemp])
      dateTypeFilter && onUpdateDate(filterdata)
      setColListTemp([])
      setAnchorColEl(null)
      if (showLoaderFn) {
        showLoaderFn(true)
        setTimeout(() => {
          showLoaderFn(false)
        }, 1000)
      }
      if (onClose) {
        onClose()
      }
    } else if (type === 'cancel') {
      setColListTemp([])
      setAnchorColEl(null)
      if (onClose) {
        onClose()
      }
    } else {
      setAnchorColEl(null)
    }
    setSearchInput('')
  }

  const handleShowList = (event: any) => {
    if (listItems?.length) {
      const tempList = JSON.parse(JSON.stringify(listItems))
      setColListTemp(
        tempList.map((item: any) => ({ ...item, select: item.select || item.viewInListing })),
      )
      setAnchorColEl(event.currentTarget)
    } else if (dateTypeFilter) {
      setAnchorColEl(event.currentTarget)
    }
  }

  let filteredData = colListTemp
  const isSelectedItems = dateTypeFilter
    ? filterdata?.filterValue || filterdata?.filterValueTo
    : filteredData?.some((item: any) => item?.select)
  if (searchInput) {
    filteredData = colListTemp.filter(
      (itemObj: any) => itemObj?.label?.toLowerCase().includes(searchInput?.toLowerCase()),
    )
  }
  const isItemsSelected = showBorder && listItems.filter((item: any) => item.select).length

  return (
    <div style={{ width: 'max-content' }} {...(key ? { key } : {})}>
      <div
        className={` d-flex align-items-center cursor-pointer add-column-container filter-l-container${
          isItemsSelected || selectedDateFilter?.length
            ? ' filter-l-selected'
            : !isPopup && ' normal-border'
        }`}
        data-testid='filter_search_with_list'
        onClick={handleShowList}
      >
        {isPopup ? (
          <Sort />
        ) : (
          <>
            {' '}
            <span
              data-testid={`${title === CONSTANTS.FILTER_ADD_COLUMN ? 'add-col' : ''}`}
              className={`font-14 ${
                isItemsSelected || selectedDateFilter?.length ? 'col1' : 'col34'
              }`}
            >
              {title}
            </span>
            <span>
              {anchorColEl ? (
                <KeyboardArrowUp className='arrow-icon' />
              ) : (
                <KeyboardArrowDown className='arrow-icon' />
              )}
            </span>
          </>
        )}
      </div>
      {anchorColEl && (
        <Menu
          className={`filter-l-dropdown-menu  ${
            dateTypeFilter ? 'border-12 c-sort common-table-filter-date' : ''
          }`}
          anchorEl={anchorColEl as any}
          open={true}
          onClose={handleClose}
        >
          {search && (
            <div className='filter-l-search'>
              <FilterSearch
                placeholder={search}
                onChange={(e: any) => {
                  setSearchInput(e.target.value)
                }}
              />
            </div>
          )}
          {dropdownTitle && (
            <div className='filter-l-title-clear'>
              {dropdownTitle && <div>{dropdownTitle}</div>}
            </div>
          )}
          {dateTypeFilter && (
            <div className='cursor-pointer'>
              <Typography className='filter-label'>Filter</Typography>
              <div className='d-flex justify-content-start align-items-center flex-column'>
                <SimpleDropdown
                  handleChange={(e) => {
                    setFilterData({ ...filterdata, filterCondition: e.target.value })
                  }}
                  fullWidth
                  label={filterConditionLabel}
                  options={filterOptions}
                  value={filterdata?.filterCondition || 'equals'}
                />
                <div>
                  <div className='mt-1'>
                    <MUIDateTimePicker
                      placement='left'
                      name={'date_second'}
                      label={filterdata?.filterCondition === 'between' ? 'From' : 'Value'}
                      type={'datetime'}
                      onChange={(e: any) => {
                        setFilterData({ ...filterdata, filterValue: e })
                      }}
                      value={filterdata?.filterValue ? new Date(filterdata.filterValue) : null}
                      error={{}}
                    />
                  </div>
                  {filterdata?.filterCondition === 'between' && (
                    <div className='mt-1'>
                      <MUIDateTimePicker
                        placement='left'
                        name={'date_second'}
                        label={'To'}
                        type={'datetime'}
                        onChange={(e: any) => {
                          setFilterData({ ...filterdata, filterValueTo: e })
                        }}
                        value={
                          filterdata?.filterValueTo ? new Date(filterdata.filterValueTo) : null
                        }
                        error={{}}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className='scroll-y filter-l-menu-item'>
            {filteredData?.map((column: any, indx: number) => (
              <div
                key={column?.label}
                className='d-flex justify-content-start align-items-center p-8 scroll-x cursor-pointer view__role-list filter-l-list-item pl-1'
                onClick={(e: any) => {
                  e.preventDefault()
                  if (isMultiple) {
                    filteredData[indx].select = !filteredData[indx].select
                  } else {
                    // Single selection logic
                    filteredData.forEach((item: any, index: number) => {
                      filteredData[index].select = index === indx ? !item.select : false
                    })
                  }
                  setColListTemp([...colListTemp])
                }}
              >
                <MUICheckbox
                  id={'check-box'}
                  handleChange={() => {
                    if (isMultiple) {
                      filteredData[indx].select = !filteredData[indx].select
                    } else {
                      // Single selection logic
                      filteredData.forEach((item: any, index: number) => {
                        filteredData[index].select = index === indx ? !item.select : false
                      })
                    }
                    setColListTemp([...filteredData])
                  }}
                  checked={column?.select || false}
                />
                <div>{column?.label}</div>
              </div>
            ))}
          </div>
          <Grid
            item
            xs={12}
            className={`d-flex justify-content-end align-items-center ${
              dateTypeFilter ? 'filter-l-list-buttons-date' : 'filter-l-list-buttons'
            } `}
          >
            {clear && (
              <Grid
                item
                xs={3}
                className='clear-text'
                data-testid='clear-all-selection'
                onClick={() => {
                  if (isSelectedItems) {
                    filteredData.forEach((item: any) => {
                      item.select = false
                    })
                    setColListTemp([...colListTemp])
                    setFilterData({ filterBy: '', filterValue: '', filterCondition: '' })
                  }
                }}
              >
                <span className={`${isSelectedItems ? '' : 'disabled_clear'} primary_ul`}>
                  Clear All
                </span>
              </Grid>
            )}
            <Grid item xs={9} className='d-flex justify-content-end'>
              {buttons.map((item: any) => (
                <span key={item.label} className={`${dateTypeFilter ? 'pl-half' : 'pl-1'}`}>
                  <MUIButton
                    disabled={item.label === 'Submit' && isEnableSubmit(dateTypeFilter, filterdata)}
                    key={item.label}
                    {...item}
                    handleClickCB={() => handleClose(item.clickAction)}
                  />
                </span>
              ))}
            </Grid>
          </Grid>
        </Menu>
      )}
    </div>
  )
}
